<!-- 2FA Setup Component -->
<div class="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
  
  <!-- Header -->
  <div class="px-6 py-4 border-b border-slate-700/50 bg-gradient-to-r from-slate-800 to-slate-800/50">
    <div class="flex items-center gap-3">
      <div class="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      </div>
      <div>
        <h3 class="text-lg font-semibold text-white">Two-Factor Authentication</h3>
        <p class="text-sm text-slate-400">Add an extra layer of security to your admin account</p>
      </div>
    </div>
  </div>

  <!-- Content -->
  <div class="p-6">
    
    <!-- Loading State -->
    <div *ngIf="isLoading && currentStep === 'status'" class="flex justify-center py-8">
      <svg class="animate-spin h-8 w-8 text-emerald-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    </div>

    <!-- Status View -->
    <div *ngIf="!isLoading && currentStep === 'status'" class="space-y-4">
      
      <!-- Status Badge -->
      <div class="flex items-center justify-between p-4 rounded-lg" 
           [ngClass]="status.enabled ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-amber-500/10 border border-amber-500/30'">
        <div class="flex items-center gap-3">
          <div class="w-3 h-3 rounded-full" [ngClass]="status.enabled ? 'bg-emerald-500' : 'bg-amber-500'"></div>
          <span class="font-medium" [ngClass]="status.enabled ? 'text-emerald-400' : 'text-amber-400'">
            {{ status.enabled ? '2FA is Enabled' : '2FA is Not Enabled' }}
          </span>
        </div>
        <svg *ngIf="status.enabled" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      </div>

      <!-- Actions -->
      <div class="flex gap-3">
        <button *ngIf="!status.enabled"
                (click)="startSetup()"
                class="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium rounded-lg transition-all duration-200 shadow-lg shadow-emerald-500/30">
          Enable 2FA
        </button>
        <button *ngIf="status.enabled"
                (click)="showDisableForm()"
                class="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-lg transition-all duration-200">
          Disable 2FA
        </button>
      </div>
    </div>

    <!-- QR Code Step -->
    <div *ngIf="currentStep === 'qr'" class="space-y-6">
      <div class="text-center">
        <h4 class="text-lg font-medium text-white mb-2">Scan QR Code</h4>
        <p class="text-sm text-slate-400">Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)</p>
      </div>

      <!-- QR Code -->
      <div class="flex justify-center">
        <div class="bg-white p-4 rounded-xl">
          <img [src]="qrCode" alt="2FA QR Code" class="w-48 h-48" />
        </div>
      </div>

      <!-- Manual Entry -->
      <div class="bg-slate-700/30 rounded-lg p-4">
        <p class="text-xs text-slate-400 mb-2">Can't scan? Enter this code manually:</p>
        <div class="flex items-center gap-2">
          <code class="flex-1 bg-slate-800 px-3 py-2 rounded font-mono text-sm text-emerald-400 break-all">{{ secret }}</code>
          <button (click)="copySecret()" class="p-2 hover:bg-slate-700 rounded-lg transition-colors" title="Copy">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Verification Input -->
      <div>
        <label class="block text-sm font-medium text-slate-300 mb-2">Enter the 6-digit code from your app:</label>
        <input 
          type="text"
          [ngModel]="verificationCode"
          (input)="onCodeInput($event, 'verificationCode')"
          maxlength="6"
          placeholder="000000"
          class="w-full text-center text-2xl font-mono tracking-[0.5em] bg-slate-700/50 border-2 border-slate-600 rounded-xl py-4 px-6 text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all duration-200"
        />
      </div>

      <!-- Error -->
      <div *ngIf="error" class="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
        {{ error }}
      </div>

      <!-- Actions -->
      <div class="flex gap-3">
        <button (click)="cancelSetup()" class="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-lg transition-all duration-200">
          Cancel
        </button>
        <button (click)="verifySetup()" 
                [disabled]="isLoading || verificationCode.length !== 6"
                class="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
          <span *ngIf="!isLoading">Verify & Enable</span>
          <span *ngIf="isLoading" class="flex items-center justify-center gap-2">
            <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Verifying...
          </span>
        </button>
      </div>
    </div>

    <!-- Backup Codes Step -->
    <div *ngIf="currentStep === 'backup'" class="space-y-6">
      <div class="text-center">
        <div class="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h4 class="text-xl font-semibold text-white mb-2">2FA Enabled Successfully!</h4>
        <p class="text-sm text-slate-400">Save your backup codes in a safe place. You'll need them if you lose access to your authenticator app.</p>
      </div>

      <!-- Warning -->
      <div class="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
        <div class="flex items-start gap-3">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p class="text-sm text-amber-400">These codes will only be shown once. Each code can only be used one time.</p>
        </div>
      </div>

      <!-- Backup Codes Grid -->
      <div class="bg-slate-700/30 rounded-lg p-4">
        <div class="grid grid-cols-2 gap-2">
          <code *ngFor="let code of backupCodes" class="bg-slate-800 px-3 py-2 rounded font-mono text-sm text-center text-slate-300">
            {{ code }}
          </code>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex gap-3">
        <button (click)="copyBackupCodes()" class="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Copy Codes
        </button>
        <button (click)="downloadBackupCodes()" class="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download
        </button>
      </div>

      <button (click)="finishSetup()" class="w-full px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium rounded-lg transition-all duration-200 shadow-lg shadow-emerald-500/30">
        I've Saved My Backup Codes
      </button>
    </div>

    <!-- Disable Step -->
    <div *ngIf="currentStep === 'disable'" class="space-y-6">
      <div class="text-center">
        <div class="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h4 class="text-lg font-medium text-white mb-2">Disable Two-Factor Authentication</h4>
        <p class="text-sm text-slate-400">Enter your current 2FA code to confirm disabling. This will make your account less secure.</p>
      </div>

      <!-- Verification Input -->
      <div>
        <label class="block text-sm font-medium text-slate-300 mb-2">Current 2FA Code:</label>
        <input 
          type="text"
          [ngModel]="disableCode"
          (input)="onCodeInput($event, 'disableCode')"
          maxlength="6"
          placeholder="000000"
          class="w-full text-center text-2xl font-mono tracking-[0.5em] bg-slate-700/50 border-2 border-slate-600 rounded-xl py-4 px-6 text-white placeholder-slate-600 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all duration-200"
        />
      </div>

      <!-- Error -->
      <div *ngIf="error" class="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
        {{ error }}
      </div>

      <!-- Actions -->
      <div class="flex gap-3">
        <button (click)="cancelDisable()" class="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-lg transition-all duration-200">
          Cancel
        </button>
        <button (click)="disable2FA()" 
                [disabled]="isLoading || disableCode.length !== 6"
                class="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
          <span *ngIf="!isLoading">Disable 2FA</span>
          <span *ngIf="isLoading" class="flex items-center justify-center gap-2">
            <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Disabling...
          </span>
        </button>
      </div>
    </div>

  </div>
</div>














